import { useState, useMemo, useRef, useCallback } from "react";
import { fuzzy_word_search } from "./utils";
import InfoTooltip from "./InfoTooltip";

const MenuFilter = ({ people, trips, person_indices_filter, trip_indices_filter, set_filter, set_filter_active, active }) => {

  const [search_text, set_search_text] = useState("");
  const input_ref = useRef(null);

  const voyage_counts = useMemo(() => {
    const counts = new Set(Object.values(people).map(p => p.person_trips.length));
    return [...counts].sort((a, b) => a - b);
  }, [people]);

  const trip_dates_list = useMemo(() => {
    return [...new Set(Object.values(trips).map(t => t.trip_date))];
  }, [trips]);

  const trip_sizes = useMemo(() => {
    const sizes = new Set(Object.values(trips).map(t => t.trip_size));
    return [...sizes].sort((a, b) => a - b);
  }, [trips]);

  // ***************************************************************************
  // Local state for filter control
  // ***************************************************************************
  // The list of people under the "By voyager" filter
  const [voyager_filter_indices, _set_voyager_filter_indices] = useState([]);
  // Indices into voyage_counts (not voyage counts themselves), so the slider
  // only steps through counts that actually occur in the data.
  const [[min_count_index, max_count_index], _set_min_max_counts_indices] = useState([0, voyage_counts.length - 1]);
  // Indices into trip_dates_list (not dates themselves), so the slider only
  // steps through dates that actually occur in the data.
  const [[min_date_index, max_date_index], _set_min_max_date_indices] = useState([0, trip_dates_list.length - 1]);
  // Indices into trip_sizes (not sizes themselves), so the slider only steps
  // through trip sizes that actually occur in the data.
  const [[min_size_index, max_size_index], _set_min_max_size_indices] = useState([0, trip_sizes.length - 1]);

  // ***************************************************************************
  // Global update_filter function, computes new values for person_indices_filter
  // and trip_indices_filter and passes them back to VoyageModel via set_filter.
  // ***************************************************************************
  const update_filter = useCallback((voyager_indices, min_count, max_count, min_date, max_date, min_size, max_size) => {
    // Create filter maps, to start with they are all set to true.
    let person_in_filter_map = Object.fromEntries(Object.keys(people).map(i => [i, true]));
    let trip_in_filter_map = Object.fromEntries(Object.keys(trips).map(i => [i, true]));

    // 1. Apply voyager filter
    for (let trip_index in trips) {
      const person_trips = trips[trip_index].person_trips;
      if (voyager_indices.length > 0 && !person_trips.some(pt => voyager_indices.includes(pt.person_index))) {
        trip_in_filter_map[trip_index] = false;
      }
    }

    // 2. Apply Voyager trip count filter
    for (let person_index in people) {
      const trip_count = people[person_index].person_trips.length;
      if (trip_count < voyage_counts[min_count] || trip_count > voyage_counts[max_count]) {
        person_in_filter_map[person_index] = false;
      }
    }

    // 3. Apply trip date filter
    for (let trip_index in trips) {
      const date_index = trip_dates_list.indexOf(trips[trip_index].trip_date);
      if (date_index < min_date || date_index > max_date) {
        trip_in_filter_map[trip_index] = false;
      }
    }

    // 4. Apply trip size filter
    for (let trip_index in trips) {
      const trip_size = trips[trip_index].trip_size;
      if (trip_size < trip_sizes[min_size] || trip_size > trip_sizes[max_size]) {
        trip_in_filter_map[trip_index] = false;
      }
    }

    let did_change = true;
    while (did_change) {
      did_change = false;
      // Remove any people that don't have any trips left in the filter
      for (let person_index in people) {
        const person_trips = people[person_index].person_trips;
        if (!person_trips.some(pt => trip_in_filter_map[pt.trip_index])) {
          did_change |= person_in_filter_map[person_index];
          person_in_filter_map[person_index] = false;
        }
      }
      // Remove any trips that don't have any people left in the filter
      for (let trip_index in trips) {
        const person_trips = trips[trip_index].person_trips;
        if (!person_trips.some(pt => person_in_filter_map[pt.person_index])) {
          did_change |= trip_in_filter_map[trip_index];
          trip_in_filter_map[trip_index] = false;
        }
      }
    }

    const person_indices_filter = Object.keys(person_in_filter_map).filter(i => person_in_filter_map[i]).map(Number);
    const trip_indices_filter = Object.keys(trip_in_filter_map).filter(i => trip_in_filter_map[i]).map(Number);
    set_filter(person_indices_filter, trip_indices_filter);

    // A filter is "active" whenever any control has been moved off its default,
    // whether or not that actually excluded anything.
    set_filter_active(voyager_indices.length > 0 ||
                      min_count > 0 || max_count < voyage_counts.length - 1 ||
                      min_date > 0 || max_date < trip_dates_list.length - 1 ||
                      min_size > 0 || max_size < trip_sizes.length - 1);
  }, [people, trips, voyage_counts, trip_dates_list, trip_sizes, set_filter, set_filter_active]);

  // ***************************************************************************
  // Local setter functions for each html element to call back.
  // Each calls update_filter to recompute the global filter.
  // ***************************************************************************
  const select_person = (index) => {
    set_search_text("");
    input_ref.current.focus();
    _set_voyager_filter_indices([...voyager_filter_indices, index]);
    update_filter([...voyager_filter_indices, index], min_count_index, max_count_index, min_date_index, max_date_index, min_size_index, max_size_index);
  };
  const deselect_person = (index) => {
    _set_voyager_filter_indices(voyager_filter_indices.filter(p => p !== index));
    update_filter(voyager_filter_indices.filter(p => p !== index), min_count_index, max_count_index, min_date_index, max_date_index, min_size_index, max_size_index);
  };
  const set_min_count_index = (index) => {
    _set_min_max_counts_indices([index, Math.max(index, max_count_index)]);
    update_filter(voyager_filter_indices, index, Math.max(index, max_count_index), min_date_index, max_date_index, min_size_index, max_size_index);
  };
  const set_max_count_index = (index) => {
    _set_min_max_counts_indices([Math.min(index, min_count_index), index]);
    update_filter(voyager_filter_indices, Math.min(index, min_count_index), index, min_date_index, max_date_index, min_size_index, max_size_index);
  };
  const set_min_date_index = (index) => {
    _set_min_max_date_indices([index, Math.max(index, max_date_index)]);
    update_filter(voyager_filter_indices, min_count_index, max_count_index, index, Math.max(index, max_date_index), min_size_index, max_size_index);
  };
  const set_max_date_index = (index) => {
    _set_min_max_date_indices([Math.min(index, min_date_index), index]);
    update_filter(voyager_filter_indices, min_count_index, max_count_index, Math.min(index, min_date_index), index, min_size_index, max_size_index);
  };
  const set_min_size_index = (index) => {
    _set_min_max_size_indices([index, Math.max(index, max_size_index)]);
    update_filter(voyager_filter_indices, min_count_index, max_count_index, min_date_index, max_date_index, index, Math.max(index, max_size_index));
  };
  const set_max_size_index = (index) => {
    _set_min_max_size_indices([Math.min(index, min_size_index), index]);
    update_filter(voyager_filter_indices, min_count_index, max_count_index, min_date_index, max_date_index, Math.min(index, min_size_index), index);
  };
  const reset_filters = () => {
    _set_voyager_filter_indices([]);
    _set_min_max_counts_indices([0, voyage_counts.length - 1]);
    _set_min_max_date_indices([0, trip_dates_list.length - 1]);
    _set_min_max_size_indices([0, trip_sizes.length - 1]);
    update_filter([], 0, voyage_counts.length - 1, 0, trip_dates_list.length - 1, 0, trip_sizes.length - 1);
  }

  const filtered_search_people = useMemo(() => {
    if (search_text.trim() === "") return [];
    return Object.values(people)
      .map((person) => {
        if (voyager_filter_indices.includes(person.person_index)) return null;
        const total_score = fuzzy_word_search(person.name.toLowerCase(), search_text);
        return total_score === null ? null : {...person, total_score };
      })
      .filter(Boolean) // Filter out null entries (no match or already selected)
      .sort((a, b) => b.total_score - a.total_score)
      .slice(0, 5);
  }, [people, search_text, voyager_filter_indices]);

  const on_search_key_down = (e) => {
    if (e.key === "Enter" && filtered_search_people.length > 0) {
      select_person(filtered_search_people[0].person_index);
    }
  };

  return (
    <div className={"menu_panel" + (active ? "" : " menu_panel_hidden")}>
      <label className="filter_label">
        By Voyager:
        <InfoTooltip visible={active}
                     text="Only show Voyages and Voyagers who have voyaged with at least one of those selected" />
      </label>
      {voyager_filter_indices.length > 0 &&
        <ul className="filter_selected_list">
          {voyager_filter_indices.map(index => (
            <li key={index}>
              <label>
                <input type="checkbox" checked onChange={() => deselect_person(index)} />
                {Object.values(people).find(p => p.person_index === index)?.name}
              </label>
            </li>
          ))}
        </ul>
      }
      <input type="text" className="menu_input filter_input" placeholder="Voyager" ref={input_ref}
             value={search_text} onChange={(e) => set_search_text(e.target.value)}
             onKeyDown={on_search_key_down} />
      {filtered_search_people.length > 0 &&
        <ul className="filter_dropdown">
          {filtered_search_people.map(item => <li key={item.person_index} onClick={() => select_person(item.person_index)}>{item.name}</li>)}
        </ul>
      }
      <label className="filter_label">By Voyager's Voyage count:</label>
      <div className="range_slider_values">{voyage_counts[min_count_index]} – {voyage_counts[max_count_index]} voyages</div>
      <div className="range_slider">
        <div className="range_slider_track" />
        <div className="range_slider_selected"
             style={{
               left: `${(min_count_index / Math.max(voyage_counts.length - 1, 1)) * 100}%`,
               right: `${100 - (max_count_index / Math.max(voyage_counts.length - 1, 1)) * 100}%`
             }} />
        <input type="range" className="range_slider_input" min={0} max={voyage_counts.length - 1}
               value={min_count_index} onChange={(e) => set_min_count_index(Number(e.target.value))} />
        <input type="range" className="range_slider_input" min={0} max={voyage_counts.length - 1}
               value={max_count_index} onChange={(e) => set_max_count_index(Number(e.target.value))} />
      </div>
      <label className="filter_label">By Voyage's date:</label>
      <div className="range_slider_values">{trip_dates_list[min_date_index]} – {trip_dates_list[max_date_index]}</div>
      <div className="range_slider">
        <div className="range_slider_track" />
        <div className="range_slider_selected"
             style={{
               left: `${(min_date_index / Math.max(trip_dates_list.length - 1, 1)) * 100}%`,
               right: `${100 - (max_date_index / Math.max(trip_dates_list.length - 1, 1)) * 100}%`
             }} />
        <input type="range" className="range_slider_input" min={0} max={trip_dates_list.length - 1}
               value={min_date_index} onChange={(e) => set_min_date_index(Number(e.target.value))} />
        <input type="range" className="range_slider_input" min={0} max={trip_dates_list.length - 1}
               value={max_date_index} onChange={(e) => set_max_date_index(Number(e.target.value))} />
      </div>
      <label className="filter_label">By Voyage's size:</label>
      <div className="range_slider_values">{trip_sizes[min_size_index]} – {trip_sizes[max_size_index]} voyagers</div>
      <div className="range_slider">
        <div className="range_slider_track" />
        <div className="range_slider_selected"
             style={{
               left: `${(min_size_index / Math.max(trip_sizes.length - 1, 1)) * 100}%`,
               right: `${100 - (max_size_index / Math.max(trip_sizes.length - 1, 1)) * 100}%`
             }} />
        <input type="range" className="range_slider_input" min={0} max={trip_sizes.length - 1}
               value={min_size_index} onChange={(e) => set_min_size_index(Number(e.target.value))} />
        <input type="range" className="range_slider_input" min={0} max={trip_sizes.length - 1}
               value={max_size_index} onChange={(e) => set_max_size_index(Number(e.target.value))} />
      </div>
      <div className="filter_reset_button" onClick={reset_filters}>Reset Filters</div>
    </div>
  );
};

export default MenuFilter;
